/**
 * The day blocks behind the phone workshop lists: a short first block from a
 * fixed start day, then a longer block each time the viewer asks for more.
 *
 * Every date here is a plain `YYYY-MM-DD` string moved by calendar-day
 * arithmetic, never read from the clock, so a list left open past midnight
 * keeps the same blocks. Each block starts the day after the previous one
 * ends, and both ends count, so nothing is skipped or fetched twice.
 */
import { addDaysUtc, MONTHS_SHORT, utcDayKey } from './workshops-calendar-dates';

export const FIRST_BLOCK_DAYS = 14;

/** Days added by each "Load more" tap. Well inside the server's range cap. */
export const NEXT_BLOCK_DAYS = 30;

export interface DayRange {
  from: string;
  to: string;
}

export function addDays(day: string, n: number): string {
  return utcDayKey(addDaysUtc(new Date(`${day}T00:00:00Z`), n));
}

/** The block that begins on `from`; the block that begins on `anchor` is the first. */
export function blockRange(anchor: string, from: string): DayRange {
  const days = from === anchor ? FIRST_BLOCK_DAYS : NEXT_BLOCK_DAYS;
  return { from, to: addDays(from, days - 1) };
}

export function nextBlockStart(anchor: string, from: string): string {
  return addDays(blockRange(anchor, from).to, 1);
}

export function formatBlockDay(day: string): string {
  const month = MONTHS_SHORT[Number(day.slice(5, 7)) - 1];
  return `${month} ${Number(day.slice(8, 10))}`;
}

/**
 * The list without repeats of the same id. The last copy stays, in its own
 * place: blocks are fetched oldest first, so the last copy is the newest answer,
 * and a workshop moved to a later block shows once at its new position.
 */
export function dedupeById<T extends { id: string }>(items: readonly T[]): T[] {
  const seen = new Set<string>();
  const newestFirst: T[] = [];
  for (let i = items.length - 1; i >= 0; i -= 1) {
    if (!seen.has(items[i].id)) {
      seen.add(items[i].id);
      newestFirst.push(items[i]);
    }
  }
  return newestFirst.reverse();
}
